// فرم هدایت‌شده از JSON Schema تنظیمات (AP-INT-02، AC-INT-002.1): زیرمجموعه type، enum، pattern و آرایه رشته‌ای. برچسب‌ها از i18n
// (تابع labelFor فراخوان)؛ اعتبارسنجی نهایی با سرور (PHX-INT-0103 با علت بدون مقدار).
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';

interface SchemaProperty {
  type?: string;
  enum?: unknown[];
  pattern?: string;
  minimum?: number;
  maximum?: number;
  items?: { type?: string };
}

/** خواندن خصوصیت‌های قابل نمایش Schema (شیء تودرتو مثل mock_scenarios نمایش داده نمی‌شود). */
export function schemaProperties(schema: unknown): [string, SchemaProperty][] {
  if (!schema || typeof schema !== 'object') {
    return [];
  }
  const properties = (schema as { properties?: Record<string, SchemaProperty> }).properties ?? {};
  return Object.entries(properties).filter(([, property]) => property.type !== 'object');
}

/** کلیدهای الزامی Schema. */
export function schemaRequired(schema: unknown): string[] {
  return schema && typeof schema === 'object' ? ((schema as { required?: string[] }).required ?? []) : [];
}

export interface SchemaFieldsProps {
  schema: unknown;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  labelFor: (key: string) => string;
  idPrefix: string;
  disabled?: boolean;
}

export function SchemaFields({ schema, value, onChange, labelFor, idPrefix, disabled = false }: SchemaFieldsProps) {
  const required = schemaRequired(schema);
  const set = (key: string, next: unknown) => {
    const copy = { ...value };
    if (next === undefined || next === '') {
      delete copy[key];
    } else {
      copy[key] = next;
    }
    onChange(copy);
  };

  return (
    <Stack spacing={2}>
      {schemaProperties(schema).map(([key, property]) => {
        const testId = `${idPrefix}-${key}`;
        if (property.type === 'boolean') {
          return (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={value[key] === true}
                  onChange={(event) => set(key, event.target.checked ? true : undefined)}
                  disabled={disabled}
                  slotProps={{ input: { 'data-testid': testId } as object }}
                />
              }
              label={labelFor(key)}
            />
          );
        }
        if (property.enum) {
          return (
            <TextField
              key={key}
              select
              label={labelFor(key)}
              value={(value[key] as string | undefined) ?? ''}
              onChange={(event) => set(key, event.target.value)}
              disabled={disabled}
              required={required.includes(key)}
              slotProps={{ htmlInput: { 'data-testid': testId } }}
            >
              {property.enum.map((option) => (
                <MenuItem key={String(option)} value={String(option)}>
                  {String(option)}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        if (property.type === 'array') {
          const current = Array.isArray(value[key]) ? (value[key] as string[]).join(', ') : '';
          return (
            <TextField
              key={key}
              label={labelFor(key)}
              defaultValue={current}
              onBlur={(event) => {
                const items = event.target.value
                  .split(',')
                  .map((item) => item.trim())
                  .filter(Boolean);
                set(key, items.length > 0 ? items : undefined);
              }}
              disabled={disabled}
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': testId } }}
            />
          );
        }
        const numeric = property.type === 'integer' || property.type === 'number';
        return (
          <TextField
            key={key}
            label={labelFor(key)}
            type={numeric ? 'number' : 'text'}
            value={value[key] === undefined ? '' : String(value[key])}
            onChange={(event) => set(key, numeric ? (event.target.value === '' ? undefined : Number(event.target.value)) : event.target.value)}
            disabled={disabled}
            required={required.includes(key)}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': testId, min: property.minimum, max: property.maximum } }}
          />
        );
      })}
    </Stack>
  );
}
